import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonCopy } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { LessonCard } from "@shared/models/card-stack"
import type { PreparedAction } from "../../action-doc-builder"
import { cloneDeep, get, omit, partial } from "lodash-es"
import Patch from "@shared/models/patch-updater"
import type { Narrative } from "../create-narrative"
import defaultLessonActivity from "./default-lesson-activity"
import defaultLessonContext from "./default-lesson-context"
import defaultLessonModels from "./default-lesson-models"

interface Payload {
  currentUserId: string
  cardStackId: string
  section: LessonCard
}

export default function lessonCardCopyAction(payload: Payload): PreparedAction {
  return {
    name: "LESSON_CARD_COPY",
    payload: payload,
    patches: patches(payload),
    undoPatches: null,
    narrative: partial(narrative, payload),
  }
}

function patches(payload: Payload) {
  let card = cloneDeep(omit(payload.section, ["id"]))
  card.attributes.commentIds = []
  let patch = Patch.create("user", payload.currentUserId).set(
    "attributes.clipboard.card",
    card
  ).patch
  return [patch]
}

function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative {
  let models = defaultLessonModels(payload, findFn, userId)
  let cardTitle = get(payload.section, "attributes.title")

  let newEvent: LessonCopy = {
    ...createBlankEvent(),
    root_id: models.planbook?.id ?? null,
    root_type: "planbook",
    date_plan_scheduled_for: models.dateStringForCardStack,
    primary_object_id: payload.cardStackId,
    primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
    primary_verb: "copy",
    primary_possessive: getPossessiveForPlanbook(findFn, userId, models.planbook?.id),
    primary_object_type: "lesson",
    primary_object_specifier: "cards",

    adverbial_verb: null,
    adverbial_object_type: null,
    adverbial_object_specifier: null,
    adverbial_object_id: null,
    adverbial_possessive: null,
    adverbial_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
  }

  return {
    title: "Lesson Card Copied",
    titleForAnalytics: "Lesson Card - Copy",
    description: `Copied the "${cardTitle}" card`,
    context: defaultLessonContext(payload, models),
    activity: defaultLessonActivity(payload, models),
    newAnalyticsEvents: [newEvent],
  }
}
